import React from "react";
import { Code2, ShoppingCart, HeartPulse, Globe, Cog, Zap, CreditCard, BarChart3, Package, Shield, Database, Workflow } from "lucide-react";

const serviceCategories = [
  {
    icon: Code2,
    title: "Software & App-Entwicklung",
    color: "#FF3B30",
    description:
      "ServicePoint Huwiler entwickelt massgeschneiderte Softwarelösungen, die auf Ihre geschäftlichen Anforderungen zugeschnitten sind. Unsere Anwendungen verbessern Arbeitsabläufe, automatisieren Prozesse und unterstützen die digitale Transformation.",
    image: "https://images.unsplash.com/photo-1771930122103-2cdf053bf9e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxzb2Z0d2FyZSUyMGRldmVsb3BtZW50JTIwY29kZSUyMHNjcmVlbiUyMGFic3RyYWN0JTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3NzI3MTE2Mjh8MA&ixlib=rb-4.1.0&q=85",
    items: [
      { icon: Globe, text: "Webapplikation-Entwicklung" },
      { icon: Cog, text: "Individuelle Unternehmenssoftware" },
      { icon: Zap, text: "API-Integrationen" },
      { icon: Workflow, text: "Automatisierung & digitale Prozessoptimierung" },
    ],
  },
  {
    icon: ShoppingCart,
    title: "E-Commerce Lösungen",
    color: "#0EA5E9",
    description:
      "Wir unterstützen Unternehmen beim Aufbau und Betrieb erfolgreicher Online-Shops und digitaler Verkaufsplattformen.",
    image: "https://images.unsplash.com/photo-1641322126277-b5e8a8a83557?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGRldmVsb3BtZW50JTIwY29kZSUyMHNjcmVlbiUyMGFic3RyYWN0JTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3NzI3MTE2Mjh8MA&ixlib=rb-4.1.0&q=85",
    items: [
      { icon: ShoppingCart, text: "Online-Shop-Entwicklung" },
      { icon: Package, text: "Marktplatz-Integration" },
      { icon: CreditCard, text: "Zahlungssystem-Integration" },
      { icon: BarChart3, text: "Produktkatalog-Management & Verkaufsoptimierung" },
    ],
  },
  {
    icon: HeartPulse,
    title: "Healthcare Digital Solutions",
    color: "#F59E0B",
    description:
      "ServicePoint Huwiler unterstützt die digitale Transformation im Gesundheitswesen mit sicheren und zuverlässigen Lösungen.",
    image: "https://images.unsplash.com/photo-1758691463610-3c2ecf5fb3fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwaGVhbHRoY2FyZSUyMHRhYmxldCUyMGRvY3RvciUyMG1pbmltYWxpc3R8ZW58MHx8fHwxNzcyNzExNjI3fDA&ixlib=rb-4.1.0&q=85",
    items: [
      { icon: HeartPulse, text: "Healthcare-Softwarelösungen" },
      { icon: Workflow, text: "Digitale Workflow-Systeme" },
      { icon: Shield, text: "Sichere digitale Anwendungen" },
      { icon: Database, text: "Datenmanagement-Lösungen" },
    ],
  },
];

export default function Services() {
  return (
    <div>
      {/* Header */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="services-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-sm font-semibold tracking-wide uppercase text-[#FF3B30]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Dienstleistungen
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0F172A] mt-3 max-w-3xl" style={{ fontFamily: 'Plus Jakarta Sans' }} data-testid="services-page-title">
            Unsere Dienstleistungen
          </h1>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed mt-6 max-w-2xl">
            Wir bieten umfassende digitale Dienstleistungen – von der Softwareentwicklung über E-Commerce bis hin zu Healthcare-Lösungen.
          </p>
        </div>
      </section>

      {/* Service Categories */}
      {serviceCategories.map((cat, idx) => (
        <section
          key={cat.title}
          className={`py-24 md:py-32 ${idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}
          data-testid={`service-category-${idx}`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${idx % 2 !== 0 ? "lg:grid-flow-dense" : ""}`}>
              {/* Text */}
              <div className={idx % 2 !== 0 ? "lg:col-start-2" : ""}>
                <div
                  className="w-12 h-12 rounded-sm flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${cat.color}10` }}
                >
                  <cat.icon size={24} style={{ color: cat.color }} strokeWidth={1.5} />
                </div>
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0F172A] mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  {cat.title}
                </h2>
                <p className="text-slate-600 leading-relaxed mb-8">
                  {cat.description}
                </p>
                <div className="space-y-4">
                  {cat.items.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 group">
                      <div
                        className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0 mt-0.5 transition-transform group-hover:scale-110"
                        style={{ backgroundColor: `${cat.color}08` }}
                      >
                        <item.icon size={16} style={{ color: cat.color }} strokeWidth={1.5} />
                      </div>
                      <span className="text-[#0F172A] text-sm font-medium pt-1">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Image */}
              <div className={idx % 2 !== 0 ? "lg:col-start-1 lg:row-start-1" : ""}>
                <div className="aspect-[4/3] rounded-sm overflow-hidden bg-slate-100">
                  <img
                    src={cat.image}
                    alt={cat.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
